import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { SITE } from "../lib/seoConfig";
import { Calendar, User, ChevronRight, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (category) => {
    setLoading(true);
    try {
      const url = category
        ? `${API}/api/blog/posts?category=${encodeURIComponent(category)}`
        : `${API}/api/blog/posts`;
      const { data } = await axios.get(url);
      setPosts(data.posts || []);
      setCategories(data.categories || []);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="bg-white min-h-screen">
      <Seo path="/blog" />
      <PublicHeader />
      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <header className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>
            WriterSpark Blog
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Strategies for essay writing, argumentative writing, narrative writing, constructed responses, and writing revision — for teachers, parents, and students.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
          <button
            data-testid="blog-cat-all"
            onClick={() => setFilter(null)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold ${
              !filter ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >All</button>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`blog-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold ${
                filter === c ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >{c}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="tactile-card p-10 text-center" data-testid="blog-empty">
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>No posts yet</h2>
            <p className="text-slate-600 text-sm">
              We're publishing soon — strategies for essay writing, argumentative writing, narrative writing, and more.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="tactile-card p-5 hover:border-indigo-300 transition flex flex-col"
              >
                {p.cover_image && (
                  <img src={p.cover_image} alt={p.title} loading="lazy"
                    className="w-full h-40 object-cover rounded-xl mb-4" />
                )}
                <span className="text-xs uppercase font-bold tracking-widest text-indigo-700 mb-2">{p.category}</span>
                <h2 className="font-bold text-lg mb-2" style={{ fontFamily: "Outfit" }}>{p.title}</h2>
                <p className="text-sm text-slate-600 mb-4 flex-1">{p.excerpt}</p>
                <div className="text-xs text-slate-400 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><User size={11}/> {p.author_name}</span>
                  <span className="inline-flex items-center gap-1"><Calendar size={11}/> {p.published_at?.slice(0,10)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// Minimal markdown → HTML (headings, bold, italic, lists, paragraphs, links)
function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split(/\r?\n/);
  let html = "";
  let inList = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (/^### /.test(line)) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h3 class="text-xl font-bold mt-6 mb-2" style="font-family:Outfit">${line.slice(4)}</h3>`;
    } else if (/^## /.test(line)) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="text-2xl font-bold mt-8 mb-3" style="font-family:Outfit">${line.slice(3)}</h2>`;
    } else if (/^# /.test(line)) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="text-3xl font-bold mt-8 mb-3" style="font-family:Outfit">${line.slice(2)}</h2>`;
    } else if (/^[-*] /.test(line)) {
      if (!inList) { html += "<ul class='list-disc pl-6 my-2 space-y-1'>"; inList = true; }
      html += `<li>${inlineFmt(line.slice(2))}</li>`;
    } else if (!line) {
      if (inList) { html += "</ul>"; inList = false; }
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<p class="my-3 leading-relaxed">${inlineFmt(line)}</p>`;
    }
  }
  if (inList) html += "</ul>";
  return html;
}
function inlineFmt(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 font-semibold underline">$1</a>');
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/blog/posts/${slug}`);
        setPost(data.post);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-white min-h-screen">
        <Seo path={`/blog/${slug}`} title="Post not found — WriterSpark" noindex />
        <PublicHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Post not found</h1>
          <Link to="/blog" className="text-indigo-600 font-bold">Back to blog</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.origin}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE.origin}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${SITE.origin}/blog/${post.slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      image: post.cover_image || `${SITE.origin}/writespark-logo.png`,
      author: { "@type": "Person", name: post.author_name },
      publisher: {
        "@type": "Organization",
        name: SITE.organization.name,
        logo: { "@type": "ImageObject", url: `${SITE.origin}/writespark-logo.png` },
      },
      datePublished: post.published_at,
      dateModified: post.updated_at,
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.origin}/blog/${post.slug}` },
      articleSection: post.category,
      keywords: post.keywords,
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <Seo
        path={`/blog/${post.slug}`}
        title={`${post.title} — WriterSpark Blog`}
        description={post.meta_description || post.excerpt}
        keywords={post.keywords}
        image={post.cover_image}
        schema={jsonLd}
      />
      <PublicHeader />
      <article className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={14}/> Back to blog
        </Link>
        <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">{post.category}</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>{post.title}</h1>
        <div className="mt-4 text-sm text-slate-500 flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><User size={13}/> {post.author_name}</span>
          <span className="inline-flex items-center gap-1"><Calendar size={13}/> {post.published_at?.slice(0,10)}</span>
        </div>
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full max-h-96 object-cover rounded-2xl mt-8" />
        )}
        <div
          data-testid="blog-body"
          className="prose prose-slate max-w-none mt-8 text-slate-700 text-[17px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
        />
      </article>
      <Footer />
    </div>
  );
}
